import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users,
  ArrowLeft,
  Package,
  Search,
  Building,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { getClientByLaundryId } from '@/pages/parametrizacion/clients/Services/clients.services';
import { fetchTotalesPorClientes } from '@/services/inventarioClientes.service';

const InventarioClientesPage = () => {
  const navigate = useNavigate();
  const { idLavanderia } = useParams();
  const { toast } = useToast();
  const [clientes, setClientes] = useState([]);
  const [filteredClientes, setFilteredClientes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [inventariosPorCliente, setInventariosPorCliente] = useState({});
  const [totalGeneralPrendas, setTotalGeneralPrendas] = useState(0);

  useEffect(() => {
    loadClientes();
  }, [idLavanderia]);

  useEffect(() => {
    filterClientes();
  }, [searchTerm, clientes]);

  const loadClientes = async () => {
    setIsLoading(true);
    try {
      const response = await getClientByLaundryId({
        query: { 
          lavanderiaId: idLavanderia,
          estado: 'Activo'
        }
      });
      
      // Asegurar que data sea un array
      const clientesData = Array.isArray(response.data) ? response.data : [];
      setClientes(clientesData);
      
      // Cargar inventario para cada cliente solo si hay clientes
      if (clientesData.length > 0) {
        await loadInventariosPorCliente(clientesData);
      }
    } catch (error) {
      console.error('Error al cargar clientes:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los clientes',
        variant: 'destructive',
      });
      setClientes([]); // Asegurar que sea un array vacío en caso de error
    } finally {
      setIsLoading(false);
    }
  };
  
  const loadInventariosPorCliente = async (clientesData) => {
    try {
      // Extraer IDs de clientes
      const clienteIds = clientesData.map(cliente => cliente._id);
      
      if (clienteIds.length === 0) {
        return;
      }
      
      // Llamar al endpoint optimizado que solo trae totales
      const result = await fetchTotalesPorClientes(clienteIds);
      
      setInventariosPorCliente(result?.totalesPorCliente || {});
      setTotalGeneralPrendas(result?.totalGeneral || 0);
    } catch (error) {
      console.error('Error al cargar inventarios:', error);
      // No mostrar toast aquí para evitar múltiples errores
      setInventariosPorCliente({});
      setTotalGeneralPrendas(0);
    }
  };

  const filterClientes = () => {
    // Asegurar que clientes sea un array
    if (!Array.isArray(clientes)) {
      setFilteredClientes([]);
      return;
    }

    if (!searchTerm.trim()) {
      setFilteredClientes(clientes);
      return;
    }

    const filtered = clientes.filter(cliente =>
      cliente.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cliente.nit?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cliente.asesor?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredClientes(filtered);
  };

  const handleClienteClick = (cliente) => {
    navigate(`/centros-lavado/${idLavanderia}/inventario-clientes/${cliente._id}`, {
      state: { cliente }
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100
      }
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-6">
      <div className="mb-4">
        <Button asChild variant="outline">
          <Link to={`/centros-lavado/${idLavanderia}`}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Volver al Centro de Lavado
          </Link>
        </Button>
      </div>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Users className="h-8 w-8 text-blue-600" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-2xl font-bold text-foreground">
                    Inventario por Cliente
                  </CardTitle>
                  <CardDescription className="text-muted-foreground">
                    Gestiona el inventario de prendas de cada cliente
                  </CardDescription>
                </div>
                {/* Total General de Prendas */}
           {/*      {totalGeneralPrendas > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <Package className="h-6 w-6 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 font-medium">Total pendientes</p>
                        <p className="text-2xl font-bold text-amber-700">{totalGeneralPrendas.toLocaleString()}</p>
                        <p className="text-xs text-gray-500">prendas por entregar</p>
                      </div>
                    </div>
                  </div>
                )} */}
              </div>
            </div>
          </CardHeader>
        </Card>
      </motion.div>

      {/* Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Card>
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar por nombre, NIT o asesor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Lista de Clientes */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                Clientes ({filteredClientes.length})
              </CardTitle>
              {isLoading && (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-sm text-gray-500 mt-4">Cargando clientes...</p>
              </div>
            ) : filteredClientes.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 font-medium mb-2">
                  {searchTerm ? 'No se encontraron clientes' : 'No hay clientes activos'}
                </p>
                <p className="text-sm text-gray-400">
                  {searchTerm ? 'Intenta con otros términos de búsqueda' : 'Parametriza clientes para este centro de lavado'}
                </p>
              </div>
            ) : (
              (() => {
                const sorted = [...filteredClientes].sort((a, b) => {
                  const pa = inventariosPorCliente[a._id] ?? 0;
                  const pb = inventariosPorCliente[b._id] ?? 0;
                  return pb - pa;
                });
                const conPendientes = sorted.filter(c => (inventariosPorCliente[c._id] ?? 0) > 0);
                const conNegativos = sorted.filter(c => (inventariosPorCliente[c._id] ?? 0) < 0);
                const sinPendientes = sorted.filter(c => (inventariosPorCliente[c._id] ?? 0) === 0);

                const renderCard = (cliente, tipo = 'normal') => {
                  const pendientes = inventariosPorCliente[cliente._id];
                  const esNegativo = tipo === 'negativo';
                  const esInactivo = tipo === 'inactivo';
                  
                  return (
                    <motion.div key={cliente._id} variants={itemVariants}>
                      <Card
                        className={`h-full cursor-pointer transition-all duration-300 border-2 ${
                          esInactivo
                            ? 'opacity-50 hover:opacity-70 border-gray-100 hover:border-gray-200'
                            : esNegativo
                            ? 'hover:shadow-lg hover:scale-[1.02] hover:border-red-300'
                            : 'hover:shadow-lg hover:scale-[1.02] hover:border-amber-300'
                        }`}
                        onClick={() => handleClienteClick(cliente)}
                      >
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between mb-2">
                            <div className={`p-2 rounded-lg ${esInactivo ? 'bg-gray-100' : esNegativo ? 'bg-red-100' : 'bg-blue-100'}`}>
                              <Users className={`h-6 w-6 ${esInactivo ? 'text-gray-400' : esNegativo ? 'text-red-600' : 'text-blue-600'}`} />
                            </div>
                            <Badge variant="outline" className={
                              esInactivo ? 'bg-gray-50 text-gray-400 border-gray-200' : 
                              esNegativo ? 'bg-red-50 text-red-700 border-red-200' : 
                              'bg-green-50 text-green-700 border-green-200'
                            }>
                              {esInactivo ? 'Sin pendientes' : esNegativo ? 'Inventario negativo' : cliente.estado}
                            </Badge>
                          </div>
                          <CardTitle className={`text-lg font-semibold leading-tight ${esInactivo ? 'text-gray-400' : 'text-gray-900'}`}>
                            {cliente.nombre}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Building className="h-4 w-4 text-gray-400" />
                            <span>NIT: {cliente.nit}</span>
                          </div>
                          {cliente.asesor && (
                            <p className="text-xs text-gray-400">Asesor: {cliente.asesor}</p>
                          )}

                          {/* Pendientes por entregar */}
                          {/* <div className={`border rounded-lg p-3 ${
                            esInactivo ? 'bg-gray-50 border-gray-200' : 
                            esNegativo ? 'bg-red-50 border-red-200' : 
                            'bg-amber-50 border-amber-200'
                          }`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Package className={`h-4 w-4 ${esInactivo ? 'text-gray-400' : esNegativo ? 'text-red-600' : 'text-amber-600'}`} />
                                <span className="text-xs text-gray-500 font-medium">
                                  {esNegativo ? 'Exceso de entregas' : 'Pendientes'}
                                </span>
                              </div>
                              <div className="text-right">
                                <p className={`text-lg font-bold ${esInactivo ? 'text-gray-400' : esNegativo ? 'text-red-700' : 'text-amber-700'}`}>
                                  {pendientes !== undefined ? Math.abs(pendientes).toLocaleString() : '...'}
                                </p>
                                <p className="text-xs text-gray-400">
                                  {esNegativo ? 'prendas entregadas de más' : 'prendas por entregar'}
                                </p>
                              </div>
                            </div>
                          </div> */}

                          <div className="pt-1">
                            <Button
                              className={`w-full text-white ${
                                esInactivo ? 'bg-gray-400 hover:bg-gray-500' : 
                                esNegativo ? 'bg-red-600 hover:bg-red-700' : 
                                'bg-blue-600 hover:bg-blue-700'
                              }`}
                              size="sm"
                            >
                              <Package className="h-4 w-4 mr-2" />
                              Ver Inventario
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                };

                return (
                  <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {conPendientes.map(c => renderCard(c, 'normal'))}
                    {conNegativos.length > 0 && (
                      <>
                        <div className="col-span-full flex items-center gap-3 mt-2">
                          <div className="flex-1 border-t border-dashed border-red-200" />
                          <span className="text-xs text-red-500 font-medium shrink-0">Inventario negativo ({conNegativos.length})</span>
                          <div className="flex-1 border-t border-dashed border-red-200" />
                        </div>
                        {conNegativos.map(c => renderCard(c, 'negativo'))}
                      </>
                    )}
                    {sinPendientes.length > 0 && (conPendientes.length > 0 || conNegativos.length > 0) && (
                      <div className="col-span-full flex items-center gap-3 mt-2">
                        <div className="flex-1 border-t border-dashed border-gray-200" />
                        <span className="text-xs text-gray-400 font-medium shrink-0">Sin pendientes ({sinPendientes.length})</span>
                        <div className="flex-1 border-t border-dashed border-gray-200" />
                      </div>
                    )}
                    {sinPendientes.map(c => renderCard(c, 'inactivo'))}
                  </motion.div>
                );
              })()
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default InventarioClientesPage;
